// tina/config.ts
import { defineConfig } from "tinacms";

// tina/collections/blog.ts
var BlogCollection = {
  name: "blog",
  label: "Blogs",
  path: "src/content/blog",
  format: "mdx",
  ui: {
    router({ document }) {
      return `/blog/${document._sys.filename}`;
    }
  },
  fields: [
    {
      type: "string",
      name: "title",
      label: "Title",
      isTitle: true,
      required: true
    },
    {
      type: "boolean",
      name: "isPublished",
      label: "Published?"
    },
    {
      name: "description",
      label: "Description",
      type: "string"
    },
    {
      name: "pubDate",
      label: "Publication Date",
      type: "datetime"
    },
    {
      name: "updatedDate",
      label: "Updated Date",
      type: "datetime"
    },
    {
      name: "heroImage",
      label: "Hero Image",
      type: "image"
    },
    {
      type: "rich-text",
      name: "body",
      label: "Body",
      isBody: true
    }
  ]
};

// tina/components/IconComponent.tsx
import React, { useState, useRef, useEffect } from "react";
import { wrapFieldsWithMeta } from "tinacms";
import * as tb from "react-icons/tb";
import { useVirtualizer } from "@tanstack/react-virtual";
var IconComponent = wrapFieldsWithMeta((props) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [containerWidth, setContainerWidth] = useState(0);
  const iconName = props.input.value;
  const iconSize = 50;
  const gapSize = 10;
  const parentRef = useRef(null);
  const filteredIcons = Object.keys(tb).filter(
    (iconKey) => iconKey.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const itemsPerRow = Math.max(
    1,
    Math.floor((containerWidth + gapSize) / (iconSize + gapSize))
  );
  const totalRows = Math.ceil(filteredIcons.length / itemsPerRow);
  const rowVirtualizer = useVirtualizer({
    count: totalRows,
    getScrollElement: () => parentRef.current,
    estimateSize: () => iconSize + gapSize,
    overscan: 5
  });
  useEffect(() => {
    const updateWidth = () => {
      if (parentRef.current) {
        setContainerWidth(parentRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);
  const handleIconSelect = (name) => {
    props.input.onChange(name);
  };
  return React.createElement("div", null, React.createElement("div", { style: { marginBottom: "1rem" } }, React.createElement("strong", null, "Selected Icon:"), " ", iconName ? React.createElement(tb[iconName], { size: 24 }) : "None"), React.createElement("div", { style: { marginBottom: "1rem" } }, React.createElement(
    "input",
    {
      type: "text",
      placeholder: "Search icons...",
      value: searchQuery,
      onChange: (e) => setSearchQuery(e.target.value),
      style: {
        width: "100%",
        padding: "8px",
        border: "1px solid #ddd",
        borderRadius: "4px"
      }
    }
  )), React.createElement(
    "div",
    {
      ref: parentRef,
      style: {
        maxHeight: "300px",
        overflowY: "auto",
        border: "1px solid #ddd",
        padding: "10px",
        borderRadius: "4px",
        position: "relative"
      }
    },
    React.createElement(
      "div",
      {
        style: {
          height: `${rowVirtualizer.getTotalSize()}px`,
          position: "relative"
        }
      },
      rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const startIndex = virtualRow.index * itemsPerRow;
        const endIndex = Math.min(
          startIndex + itemsPerRow,
          filteredIcons.length
        );
        return React.createElement(
          "div",
          {
            key: virtualRow.key,
            style: {
              position: "absolute",
              top: `${virtualRow.start}px`,
              left: 0,
              display: "flex",
              gap: `${gapSize}px`
            }
          },
          filteredIcons.slice(startIndex, endIndex).map((iconKey) => {
            const Icon = tb[iconKey];
            const isSelected = iconKey === iconName;
            return React.createElement(
              "div",
              {
                key: iconKey,
                onClick: () => handleIconSelect(iconKey),
                style: {
                  cursor: "pointer",
                  padding: "5px",
                  border: isSelected ? "2px solid blue" : "2px solid transparent",
                  borderRadius: "4px",
                  textAlign: "center",
                  width: `${iconSize}px`,
                  height: `${iconSize}px`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }
              },
              React.createElement(Icon, { size: 24 })
            );
          })
        );
      })
    )
  ));
});

// tina/collections/global-config.ts
var GlobalConfigCollection = {
  name: "config",
  label: "Global Config",
  path: "src/content/config",
  format: "json",
  ui: {
    global: true
  },
  fields: [
    {
      name: "seo",
      label: "General site config",
      type: "object",
      fields: [
        {
          name: "title",
          label: "Site title for SEO",
          type: "string",
          required: true
        },
        {
          name: "description",
          label: "Site description for SEO",
          type: "string",
          required: true
        },
        {
          name: "siteOwner",
          label: "Your Name, Company Name (Used in the footer",
          required: true,
          type: "string",
          ui: {
            defaultValue: "Your name here"
          }
        }
        // Add more settings here...
      ]
    },
    {
      name: "nav",
      label: "Site Navigation Menu (Reorder, Add, Remove)",
      type: "object",
      list: true,
      ui: {
        itemProps: (item) => {
          return {
            label: item.title
          };
        }
      },
      fields: [
        {
          name: "title",
          label: "Title of Nav Item",
          type: "string",
          required: true
        },
        {
          name: "link",
          label: "Path of the Nav Item",
          type: "string",
          required: true
        }
      ]
    },
    {
      name: "contactLinks",
      label: "Contact Links",
      type: "object",
      list: true,
      ui: {
        itemProps: (item) => {
          return {
            label: item.title
          };
        }
      },
      fields: [
        {
          name: "title",
          label: "Title",
          type: "string"
        },
        {
          name: "link",
          label: "Link",
          type: "string"
        }
        // {
        //   name: "icon",
        //   label: "Icon",
        //   type: "string",
        //   ui: {
        //     //@ts-ignore
        //     component: IconComponent
        //   }
        // }
      ]
    }
    // Add other config fields here...
  ]
};

// tina/collections/page.ts
var commonFields = [
  {
    name: "seoTitle",
    type: "string",
    required: true
  },
  {
    name: "template",
    type: "string",
    ui: { component: "hidden" }
  }
];
var HomeTemplate = {
  name: "Home",
  label: "Home",
  fields: [
    ...commonFields,
    { name: "heroVideo", type: "image", label: "Hero Video URL" },
    {
      name: "shortAbout",
      type: "rich-text",
      label: "About Me Intro",
      isBody: true
    }
  ]
};
var AboutTemplate = {
  name: "About",
  label: "About",
  fields: [
    ...commonFields,
    { name: "body", type: "rich-text", label: "Body", isBody: true }
  ]
};
var disciplineFields = [
  {
    name: "body",
    label: "Body",
    type: "rich-text"
  },
  {
    name: "color",
    label: "Color",
    type: "string",
    options: ["pink", "lilac", "orange", "white", "black", "blue"],
    description: "Background colour for dialog"
  }
];
var WorkWithMeTemplate = {
  name: "WorkWithMe",
  label: "WorkWithMe",
  fields: [
    ...commonFields,
    { name: "bio", type: "rich-text", label: "Bio", isBody: true },
    {
      name: "bioImage",
      type: "image",
      label: "Image",
      description: "Will be cropped to 16:9 aspect ratio"
    },
    {
      name: "shortSection",
      type: "rich-text",
      label: "Short Section",
      description: "Goes underneath image and above brands"
    },
    {
      name: "brands",
      type: "image",
      list: true,
      label: "Brand logos",
      description: "For best results, use .svg files"
    },
    {
      name: "thumbnails",
      type: "image",
      list: true,
      label: "Thumbnails",
      description: "Thumbnails for the online community videos"
    },
    {
      name: "intro",
      type: "string",
      label: "Intro",
      description: "Introduction to work with me page"
    },
    {
      name: "whatYouGet",
      type: "string",
      label: "What You Get",
      description: "Top of work with me page",
      list: true
    },
    {
      name: "disciplines",
      label: "Disciplines",
      type: "object",
      list: true,
      templates: [
        {
          name: "yoga",
          label: "Yoga",
          fields: [
            ...disciplineFields,
            {
              name: "name",
              label: "Name",
              type: "string",
              options: ["yoga"],
              ui: {
                component: "hidden"
              }
            }
          ]
        },
        {
          name: "strength",
          label: "Strength",
          fields: [
            ...disciplineFields,
            {
              name: "name",
              label: "Name",
              type: "string",
              options: ["strength"],
              ui: {
                component: "hidden"
              }
            }
          ]
        },
        {
          name: "skills",
          label: "Skills",
          fields: [
            ...disciplineFields,
            {
              name: "name",
              label: "Name",
              type: "string",
              options: ["skills"],
              ui: {
                component: "hidden"
              }
            }
          ]
        },
        {
          name: "movement",
          label: "Movement",
          fields: [
            ...disciplineFields,
            {
              name: "name",
              label: "Name",
              type: "string",
              options: ["movement"],
              ui: {
                component: "hidden"
              }
            }
          ]
        }
      ]
    }
  ]
};
var EventsTemplate = {
  name: "Events",
  label: "Events",
  fields: [
    ...commonFields,
    {
      name: "noticeText",
      label: "Notice",
      type: "string"
    },
    {
      name: "noticeWarning",
      label: "Notice Warning",
      type: "string"
    },
    {
      name: "playlist",
      label: "Playlist",
      type: "string",
      description: "URL to Spotify playlist"
    }
  ]
};
var PageCollection = {
  name: "page",
  label: "Pages",
  path: "src/content/page",
  format: "mdx",
  ui: {
    router: ({ document }) => {
      console.log("document", document);
      if (document._sys.filename === "home") return "/";
      return `/${document._sys.filename}`;
    }
  },
  templates: [
    HomeTemplate,
    AboutTemplate,
    WorkWithMeTemplate,
    EventsTemplate
  ]
};

// tina/components/MultiDatePicker.tsx
import React2 from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
var MultiDatePicker = ({
  input
}) => {
  const value = Array.isArray(input.value) ? input.value : [];
  const selectedDates = value.map((dateStr) => new Date(dateStr));
  const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const isSelected = (date) => selectedDates.some((d) => isSameDay(d, date));
  const toggleDate = (date) => {
    const exists = selectedDates.find((d) => isSameDay(d, date));
    const newDates = exists ? selectedDates.filter((d) => !isSameDay(d, date)) : [...selectedDates, date];
    const validIsoDates = newDates.filter((d) => !isNaN(d.getTime())).map((d) => d.toDateString());
    input.onChange({
      target: { value: validIsoDates }
    });
  };
  return React2.createElement("div", null, React2.createElement("label", { className: "block" }, "Except on these dates"), React2.createElement(
    DatePicker,
    {
      inline: true,
      onChange: () => {
      },
      dayClassName: (date) => isSelected(date) ? "" : "",
      renderDayContents: (day, date) => React2.createElement(
        "div",
        {
          onClick: (e) => {
            e.preventDefault();
            console.log("I clicked on a day");
            toggleDate(date);
          },
          style: {
            cursor: "pointer",
            backgroundColor: isSelected(date) ? "#93c5fd" : void 0,
            borderRadius: "0.3em"
          }
        },
        day
      )
    }
  ), React2.createElement("div", { className: "mt-3 text-sm" }, React2.createElement("strong", null, "Selected Dates:"), React2.createElement("ul", { className: "list-disc pl-5" }, selectedDates.map((d, i) => React2.createElement("li", { key: i }, d.toDateString())))));
};

// tina/collections/event.ts
var commonFields2 = [
  {
    name: "title",
    type: "string",
    label: "Title",
    required: true
  },
  {
    name: "image",
    type: "image",
    label: "Image",
    description: "Used on event page"
  },
  {
    name: "bannerImage",
    type: "image",
    label: "Banner Image",
    description: "Used for highlight - should contain the event title typography"
  },
  {
    name: "isHighlight",
    type: "boolean",
    label: "Is highlighted?"
  },
  {
    name: "template",
    type: "string",
    ui: { component: "hidden" }
  },
  {
    name: "summary",
    type: "rich-text",
    isBody: true,
    label: "Summary"
  },
  {
    name: "location",
    type: "string",
    label: "Location"
  },
  {
    name: "startTime",
    type: "datetime",
    label: "Start Time",
    description: "NOTE: this will be assumed to be UK time, whether that&apos;s GMT or BST in the summer.",
    ui: {
      timeFormat: "HH:mm"
    }
  },
  {
    name: "endTime",
    type: "datetime",
    label: "End Time",
    description: "NOTE: this will be assumed to be UK time, whether that&apos; GMT or BST in the summer.",
    ui: {
      timeFormat: "HH:mm"
    }
  },
  {
    name: "link",
    type: "object",
    label: "Link",
    fields: [
      {
        name: "linkUrl",
        type: "string",
        label: "Link",
        description: "E.g. EventBrite Link"
      },
      {
        name: "linkText",
        type: "string",
        label: "Text",
        description: 'e.g. "Get tickets"'
      }
    ]
  }
];
var OneOffEventTemplate = {
  name: "oneOffEvent",
  label: "One-off Event",
  fields: [...commonFields2]
};
var RepeatingEventTemplate = {
  name: "repeatingEvent",
  label: "Repeating Event",
  fields: [
    ...commonFields2,
    {
      name: "frequency",
      label: "Frequency",
      type: "object",
      description: "How often this event is going to occur",
      fields: [
        {
          name: "every",
          label: "Every...",
          type: "string",
          options: [
            {
              label: "Week",
              value: "week"
            },
            {
              label: "Month",
              value: "month"
            }
          ],
          ui: {
            component: "select"
          }
        },
        {
          name: "weekday",
          label: "If every week, on this day",
          type: "string",
          options: [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday"
          ].map((entry, i) => {
            return { label: entry, value: i.toString() };
          }),
          ui: {
            component: "select"
          }
        },
        {
          name: "monthlyDate",
          label: "If every month, on this date",
          type: "string",
          options: Array(31).fill("").map((val, i) => i + 1).map((val) => {
            return {
              label: val.toString(),
              value: val.toString()
            };
          }),
          ui: {
            component: "select"
          }
        },
        {
          name: "repeatUntil",
          label: "Repeat until this date (inclusive)",
          type: "datetime"
        },
        {
          label: "Exception Dates",
          name: "exceptionDates",
          type: "string",
          list: true,
          ui: {
            component: MultiDatePicker
          }
        }
      ]
    }
  ]
};
var EventCollection = {
  name: "event",
  label: "Events",
  path: "src/content/event",
  format: "mdx",
  ui: {
    router: ({ document }) => {
      return `/events/${document._sys.filename}`;
    }
  },
  templates: [OneOffEventTemplate, RepeatingEventTemplate]
};

// tina/collections/review.ts
var ReviewCollection = {
  name: "review",
  label: "Reviews",
  path: "src/content/reviews",
  format: "mdx",
  fields: [
    {
      type: "string",
      name: "author",
      label: "Author",
      isTitle: true,
      required: true
    },
    {
      type: "string",
      name: "source",
      label: "Source"
    },
    {
      type: "boolean",
      name: "isPublished",
      label: "Published?"
    },
    {
      type: "rich-text",
      name: "body",
      label: "Body",
      isBody: true
    }
  ]
};

// tina/config.ts
var branch = process.env.GITHUB_BRANCH || process.env.VERCEL_GIT_COMMIT_REF || process.env.HEAD || "main";
var config_default = defineConfig({
  branch,
  // Get this from tina.io
  clientId: process.env.PUBLIC_TINA_CLIENT_ID,
  // Get this from tina.io
  token: process.env.TINA_TOKEN,
  build: {
    outputFolder: "admin",
    publicFolder: "public"
  },
  media: {
    tina: {
      mediaRoot: "",
      publicFolder: "public"
    }
  },
  // See docs on content modeling for more info on how to setup new content models: https://tina.io/docs/schema/
  schema: {
    collections: [
      BlogCollection,
      PageCollection,
      EventCollection,
      ReviewCollection,
      GlobalConfigCollection
    ]
  }
});
export {
  config_default as default
};
